import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import { ExportButtons } from "@/lib/exportData";
import { isLegacyStoreContent } from "@/lib/xplosivContent";

const empty = { title: "", subtitle: "", image: "", cta_label: "Explore occasions", cta_link: "/services", position: "hero", is_active: true, sort_order: 0 };

export default function AdminBanners() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = async () => setItems((await api.get("/admin/banners")).data.filter((item) => !isLegacyStoreContent(item)));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing) await api.put(`/admin/banners/${editing}`, form);
            else await api.post("/admin/banners", form);
            toast.success("Saved"); setEditing(null); setForm(empty); load();
        } catch (e) { toast.error("Save failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete banner?")) return; await api.delete(`/admin/banners/${id}`); load(); };

    return (
        <div data-testid="admin-banners-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <h1 className="font-display text-4xl">Banners</h1>
                <ExportButtons rows={items} filename="xplosiv_event_banners" columns={["position", "title", "subtitle", "image", "cta_label", "cta_link", "is_active", "sort_order"]} />
            </div>
            <form onSubmit={save} className="mt-6">
                <Card className="rounded-[20px] border-[#E7DED2] bg-white/80">
                    <CardContent className="p-6 grid sm:grid-cols-2 gap-3">
                        <Fld label="Title"><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required /></Fld>
                        <Fld label="Subtitle"><Input value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} /></Fld>
                        <div className="sm:col-span-2">
                            <Label className="font-mono-ui mb-1.5 block">Banner image</Label>
                            <ImageUploader value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
                        </div>
                        <Fld label="CTA label"><Input value={form.cta_label} onChange={e => setForm({ ...form, cta_label: e.target.value })} /></Fld>
                        <Fld label="CTA link"><Input value={form.cta_link} onChange={e => setForm({ ...form, cta_link: e.target.value })} /></Fld>
                        <Fld label="Position">
                            <Select value={form.position} onValueChange={v => setForm({ ...form, position: v })}>
                                <SelectTrigger className="rounded-2xl"><SelectValue /></SelectTrigger>
                                <SelectContent>{[{ value: "hero", label: "Homepage hero" }, { value: "drop", label: "Featured event" }, { value: "lookbook", label: "Event story" }, { value: "slim", label: "Announcement strip" }].map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </Fld>
                        <Fld label="Sort order"><Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: Number(e.target.value) })} /></Fld>
                        <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} /></div>
                        <div className="sm:col-span-2 flex gap-3">
                            <button type="submit" className="dw-btn-primary px-5 py-2 font-mono-ui" data-testid="admin-banner-save">{editing ? "Update" : "Add banner"}</button>
                            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="dw-btn-secondary px-5 py-2 font-mono-ui">Cancel</button>}
                        </div>
                    </CardContent>
                </Card>
            </form>

            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(b => (
                    <Card key={b.id} className="rounded-[20px] border-[#E7DED2] bg-white/80 overflow-hidden">
                        {b.image && <img src={b.image} alt="" className="aspect-[16/9] w-full object-cover" />}
                        <CardContent className="p-4">
                            <p className="font-mono-ui text-xs text-muted-foreground">{b.position} · {b.is_active ? "Active" : "Hidden"}</p>
                            <h3 className="font-medium mt-1">{b.title}</h3>
                            <p className="text-xs text-muted-foreground line-clamp-2">{b.subtitle}</p>
                            <div className="mt-3 flex gap-2">
                                <button onClick={() => { setEditing(b.id); setForm(b); }} className="text-xs font-mono-ui underline"><Pencil className="h-3 w-3 inline" /> Edit</button>
                                <button onClick={() => del(b.id)} className="text-xs font-mono-ui text-[#B42318]"><Trash2 className="h-3 w-3 inline" /> Delete</button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}

function Fld({ label, children }) { return <div><Label className="font-mono-ui mb-1.5 block">{label}</Label>{children}</div>; }
